/**
 * Create Ticket Page - Redesigned
 *
 * Modern, user-friendly form for creating support tickets
 */

import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { createTicket, uploadTicketAttachment } from '@/services/tickets';
import api from '@/services/api';
import type { CreateTicketData, TicketCategory, TicketPriority, Account } from '@/types';

export function CreateTicketPage() {
  const { t } = useTranslation('tickets');
  const navigate = useNavigate();
  const { user } = useAuth();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);

  // Categories and priorities
  const categories: { value: TicketCategory; label: string; icon: string; color: string }[] = [
    { value: 'technical', label: t('category.technical'), icon: '⚙️', color: 'text-blue-600' },
    { value: 'billing', label: t('category.billing'), icon: '💳', color: 'text-green-600' },
    { value: 'feature_request', label: t('category.feature_request'), icon: '✨', color: 'text-purple-600' },
    { value: 'bug_report', label: t('category.bug_report'), icon: '🐛', color: 'text-red-600' },
    { value: 'account', label: t('category.account'), icon: '👤', color: 'text-indigo-600' },
    { value: 'other', label: t('category.other'), icon: '📌', color: 'text-gray-600' },
  ];

  const priorities: { value: TicketPriority; label: string; color: string }[] = [
    { value: 'low', label: t('priority.low'), color: 'bg-gray-100 text-gray-700' },
    { value: 'medium', label: t('priority.medium'), color: 'bg-blue-100 text-blue-700' },
    { value: 'high', label: t('priority.high'), color: 'bg-orange-100 text-orange-700' },
    { value: 'urgent', label: t('priority.urgent'), color: 'bg-red-100 text-red-700' },
  ];

  const [formData, setFormData] = useState<CreateTicketData>({
    subject: '',
    message: '',
    category: 'other',
    priority: 'medium',
    campaign_id: null,
  });

  const [errors, setErrors] = useState<Partial<Record<keyof CreateTicketData, string>>>({});

  // Fetch accounts for admin users
  useEffect(() => {
    if (user?.role === 'admin') {
      const fetchAccounts = async () => {
        try {
          setLoadingAccounts(true);
          const response = await api.get<{ data: Account[] }>('/api/admin/accounts');
          setAccounts(response.data.data);
        } catch (error) {
          console.error('Failed to fetch accounts:', error);
        } finally {
          setLoadingAccounts(false);
        }
      };
      fetchAccounts();
    }
  }, [user]);

  const handleChange = (
    field: keyof CreateTicketData,
    value: string | TicketCategory | TicketPriority | number | null
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const filesArray = Array.from(e.target.files);
      setSelectedFiles((prev) => [...prev, ...filesArray]);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    if (e.dataTransfer.files) {
      const filesArray = Array.from(e.dataTransfer.files);
      setSelectedFiles((prev) => [...prev, ...filesArray]);
    }
  };

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    const newErrors: Partial<Record<keyof CreateTicketData, string>> = {};

    if (!formData.subject.trim()) {
      newErrors.subject = t('validation.subjectRequired');
    }

    if (!formData.message.trim()) {
      newErrors.message = t('validation.messageRequired');
    }

    // Admin must select an account
    if (user?.role === 'admin' && !formData.account_id) {
      newErrors.account_id = t('validation.accountRequired');
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Create the ticket first
      const response = await createTicket(formData);
      const ticketId = response.ticket.id;

      // Upload attachments if any
      if (selectedFiles.length > 0) {
        const uploadPromises = selectedFiles.map((file) =>
          uploadTicketAttachment(ticketId, file).catch((err) => {
            console.error(`Failed to upload file ${file.name}:`, err);
            return null; // Continue with other uploads even if one fails
          })
        );

        await Promise.all(uploadPromises);
      }

      // Navigate to the newly created ticket
      navigate(`/tickets/${ticketId}`);
    } catch (err: any) {
      setError(err.response?.data?.message || t('notifications.failedToCreate'));
      console.error('Error creating ticket:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/30 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav className="mb-6">
          <Link
            to="/tickets"
            className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-medium transition-colors group"
          >
            <svg className="w-5 h-5 transition-transform group-hover:-translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            {t('create.backToList')}
          </Link>
        </nav>

        {/* Header with Gradient */}
        <div className="mb-8 relative">
          <div className="absolute inset-0 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-3xl opacity-5 blur-3xl" />
          <div className="relative">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg">
                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-gray-900 via-indigo-900 to-purple-900 bg-clip-text text-transparent">
                  {t('create.title')}
                </h1>
              </div>
            </div>
            <p className="text-gray-600 ml-15">
              {t('create.subtitle')}
            </p>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 rounded-2xl border-2 border-red-200 bg-gradient-to-br from-red-50 to-red-100/50 p-5 shadow-sm animate-shake">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <div className="flex-1">
                <p className="text-red-800 font-medium">{error}</p>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl border-2 border-gray-100 shadow-lg shadow-gray-200/50 overflow-hidden">
              <form onSubmit={handleSubmit} className="p-6 md:p-8 space-y-6">
                {/* Account Selector (Admin Only) */}
                {user?.role === 'admin' && (
                  <div>
                    <label htmlFor="account_id" className="block text-sm font-semibold text-gray-900 mb-2">
                      {t('form.account')} <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="account_id"
                      className={`w-full px-4 py-3 border-2 rounded-xl transition-all focus:outline-none focus:ring-4 focus:ring-indigo-500/20 ${
                        errors.account_id
                          ? 'border-red-300 bg-red-50'
                          : 'border-gray-200 hover:border-gray-300 focus:border-indigo-500'
                      }`}
                      value={formData.account_id || ''}
                      onChange={(e) => handleChange('account_id', e.target.value ? Number(e.target.value) : null)}
                      disabled={isLoading || loadingAccounts}
                    >
                      <option value="">{t('form.accountPlaceholder')}</option>
                      {accounts.map((account) => (
                        <option key={account.id} value={account.id}>
                          {account.name} (ID: {account.id})
                        </option>
                      ))}
                    </select>
                    {errors.account_id && (
                      <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        {errors.account_id}
                      </p>
                    )}
                    {loadingAccounts && (
                      <p className="mt-2 text-sm text-gray-500 flex items-center gap-2">
                        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        {t('form.loadingAccounts')}
                      </p>
                    )}
                  </div>
                )}

                {/* Subject */}
                <div>
                  <label htmlFor="subject" className="block text-sm font-semibold text-gray-900 mb-2">
                    {t('form.subject')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="subject"
                    className={`w-full px-4 py-3 border-2 rounded-xl transition-all focus:outline-none focus:ring-4 focus:ring-indigo-500/20 ${
                      errors.subject
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-200 hover:border-gray-300 focus:border-indigo-500'
                    }`}
                    value={formData.subject}
                    onChange={(e) => handleChange('subject', e.target.value)}
                    placeholder={t('form.subjectPlaceholder')}
                    disabled={isLoading}
                  />
                  {errors.subject && (
                    <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {errors.subject}
                    </p>
                  )}
                </div>

                {/* Category Grid */}
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-3">
                    {t('form.category')} <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {categories.map((cat) => (
                      <button
                        key={cat.value}
                        type="button"
                        onClick={() => handleChange('category', cat.value)}
                        disabled={isLoading}
                        className={`p-4 rounded-xl border-2 transition-all text-left ${
                          formData.category === cat.value
                            ? 'border-indigo-500 bg-indigo-50 shadow-md scale-105'
                            : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-2xl">{cat.icon}</span>
                          {formData.category === cat.value && (
                            <svg className="w-5 h-5 text-indigo-600 ml-auto" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                        <div className={`text-sm font-medium ${cat.color}`}>
                          {cat.label}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Priority */}
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-3">
                    {t('form.priority')}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {priorities.map((pri) => (
                      <button
                        key={pri.value}
                        type="button"
                        onClick={() => handleChange('priority', pri.value)}
                        disabled={isLoading}
                        className={`px-4 py-2 rounded-full font-medium text-sm transition-all ${
                          formData.priority === pri.value
                            ? `${pri.color} ring-2 ring-offset-2 ring-current scale-105`
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {pri.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-900 mb-2">
                    {t('form.message')} <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    className={`w-full px-4 py-3 border-2 rounded-xl transition-all focus:outline-none focus:ring-4 focus:ring-indigo-500/20 ${
                      errors.message
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-200 hover:border-gray-300 focus:border-indigo-500'
                    }`}
                    value={formData.message}
                    onChange={(e) => handleChange('message', e.target.value)}
                    placeholder={t('form.messagePlaceholder')}
                    rows={8}
                    disabled={isLoading}
                  />
                  {errors.message && (
                    <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {errors.message}
                    </p>
                  )}
                </div>

                {/* File Upload - Drag & Drop */}
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-3">
                    {t('form.attachmentsOptional')}
                  </label>
                  <div
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all ${
                      isDragging
                        ? 'border-indigo-500 bg-indigo-50 scale-105'
                        : 'border-gray-300 hover:border-gray-400 bg-gray-50'
                    }`}
                  >
                    <input
                      type="file"
                      id="file-upload"
                      className="hidden"
                      onChange={handleFileChange}
                      multiple
                      accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif,.zip"
                      disabled={isLoading}
                    />
                    <label htmlFor="file-upload" className="cursor-pointer">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                        <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                      </div>
                      <div className="text-sm">
                        <span className="font-semibold text-indigo-600 hover:text-indigo-700">
                          {t('form.chooseFiles')}
                        </span>
                        <span className="text-gray-600"> or drag and drop</span>
                      </div>
                      <p className="mt-2 text-xs text-gray-500">
                        {t('form.fileFormats')}
                      </p>
                    </label>
                  </div>

                  {/* Selected Files List */}
                  {selectedFiles.length > 0 && (
                    <div className="mt-4 space-y-2">
                      {selectedFiles.map((file, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-3 p-3 bg-white border-2 border-gray-200 rounded-xl hover:border-gray-300 transition-colors group"
                        >
                          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                            <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                            <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeFile(index)}
                            className="w-8 h-8 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100"
                            disabled={isLoading}
                          >
                            <svg className="w-5 h-5 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Submit Button */}
                <div className="flex justify-end pt-4">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="px-8 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 min-h-[48px] min-w-[150px]"
                  >
                    {isLoading ? (
                      <span className="flex items-center gap-2">
                        <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        {t('actions.creating')}
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        {t('actions.createTicket')}
                      </span>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Sidebar - Help Tips */}
          <div className="lg:col-span-1">
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border-2 border-blue-100 p-6 shadow-lg shadow-blue-100/50 sticky top-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-md">
                  <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-bold text-blue-900">
                  {t('create.helpTitle')}
                </h3>
              </div>
              <ul className="space-y-3">
                {[
                  t('create.helpTip1'),
                  t('create.helpTip2'),
                  t('create.helpTip3'),
                  t('create.helpTip4'),
                ].map((tip, index) => (
                  <li key={index} className="flex items-start gap-3 text-sm text-blue-900">
                    <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span className="flex-1">{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
